import { PaginatedParams } from '@/@types/common'
import {
  CreateDepartmentPayload,
  CreateEmployeeProps,
  CreateTeam,
  CreateTeamEmployee,
  CreateTeamLeader,
  CreateTeamOfficeManager,
  DeleteTeamEmployee,
  DepartmentsType,
  EmployeeFullProfile,
  EmployeeResponse,
  IdShortCodeProps,
  ShortCodeProps,
  TeamByIdProps,
  TeamMemberResponse,
  TeamsResponseType,
  UpdateDepartmentPayload,
  UpdateEmployeeProfilePictureProps,
  UpdateEmployeeProps,
  UpdateTeam,
} from '@/@types/staff'
import { getCommonUrl } from '@/hooks/common/common-api-url'
import {
  getPrepareHeaders,
  staffAdminAppkeyParam,
  vbUrl,
} from '@/hooks/common/common-functions'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export const staffApi = createApi({
  reducerPath: 'staff',
  baseQuery: fetchBaseQuery({
    baseUrl: vbUrl + 'vb-apps/staff/admin',
    prepareHeaders: (headers) => getPrepareHeaders({ headers }),
  }),
  tagTypes: ['Employees', 'Departments', 'Teams', 'TeamEmployees', 'Roles'],
  endpoints: (builder) => ({
    getEmployees: builder.query<EmployeeResponse[], string>({
      query: (venue_short_code) => ({
        url: getCommonUrl({
          queryString: '/employees',
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['Employees'],
    }),

    getDepartments: builder.query<DepartmentsType, PaginatedParams>({
      query: ({ venue_short_code, page, size }) => ({
        url: getCommonUrl({
          queryString: '/departments',
          query: `&venue_short_code=${venue_short_code}&page=${page}&per_page=${size}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['Departments'],
    }),

    createDepartment: builder.mutation<void, CreateDepartmentPayload>({
      query: (payload) => ({
        url: getCommonUrl({
          queryString: '/departments',
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${payload.short_code}`,
        }),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Departments'],
    }),

    addEmployee: builder.mutation<void, CreateEmployeeProps>({
      query: ({ short_code, employeeData }) => ({
        url: getCommonUrl({
          queryString: '/employees',
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: employeeData,
        formData: true, // Add this
      }),
      invalidatesTags: ['Employees'],
    }),

    updateEmployee: builder.mutation<void, UpdateEmployeeProps>({
      query: ({ id, short_code, employeeData }) => ({
        url: getCommonUrl({
          queryString: `/employees-update/${id}`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: employeeData,
        formData: true, // Add this
      }),
      invalidatesTags: ['Employees'],
    }),

    // Team employee
    getEmployeeById: builder.query<EmployeeFullProfile, IdShortCodeProps>({
      query: ({ id, venue_short_code }) => ({
        url: getCommonUrl({
          queryString: `/employees/full/${id}`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
    }),

    getTeamEmployees: builder.query<TeamMemberResponse[], IdShortCodeProps>({
      query: ({ venue_short_code, id }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}/employees`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['TeamEmployees'],
    }),
    getTeamleaders: builder.query<EmployeeResponse[], ShortCodeProps>({
      query: ({ venue_short_code }) => ({
        url: getCommonUrl({
          queryString: `/employees/team-leaders`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['TeamEmployees'],
    }),
    getOperationsManager: builder.query<EmployeeResponse[], ShortCodeProps>({
      query: ({ venue_short_code }) => ({
        url: getCommonUrl({
          queryString: `/employees/operations-managers`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['TeamEmployees'],
    }),

    getTeamById: builder.query<TeamByIdProps, IdShortCodeProps>({
      query: ({ venue_short_code, id }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['TeamEmployees'],
    }),

    getDepartmentsByTeam: builder.query<TeamByIdProps, IdShortCodeProps>({
      query: ({ venue_short_code, id }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}/departments`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['TeamEmployees'],
    }),

    createTeamEmployee: builder.mutation<void, CreateTeamEmployee>({
      query: ({ short_code, id, employees_ids }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}/assign-employees`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: { employee_ids: employees_ids },
      }),
      invalidatesTags: ['TeamEmployees'],
    }),

    // Operation Manager

    createTeamOfficeManager: builder.mutation<void, CreateTeamOfficeManager>({
      query: ({ short_code, employee_id, team_ids }) => ({
        url: getCommonUrl({
          queryString: 'teams/assign-operations-manager',
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: { team_ids, employee_id },
      }),
      invalidatesTags: ['TeamEmployees', 'Teams'],
    }),

    updateDepartment: builder.mutation<void, UpdateDepartmentPayload>({
      query: (payload) => ({
        url: getCommonUrl({
          queryString: `/departments/${payload.id}`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${payload.short_code}`,
        }),
        method: 'PUT',
        body: {
          name: payload.name,
          description: payload.description,
        },
      }),
      invalidatesTags: ['Departments'],
    }),

    deleteDepartment: builder.mutation<
      void,
      { id: number; short_code: string }
    >({
      query: ({ id, short_code }) => ({
        url: getCommonUrl({
          queryString: `/departments/${id}`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: ['Departments'],
    }),

    getStaffById: builder.query<unknown, number>({
      query: (id) => ({
        url: `/employees/${id}`,
      }),
      providesTags: ['Employees'],
    }),

    deleteEmployee: builder.mutation<number, number>({
      query: (id) => ({
        url: `/employee/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Employees'],
    }),

    // Teams
    getTeams: builder.query<TeamsResponseType, PaginatedParams>({
      query: ({ venue_short_code, page, size }) => ({
        url: getCommonUrl({
          queryString: '/teams',
          query: `&venue_short_code=${venue_short_code}&page=${page}&per_page=${size}`,
          params: staffAdminAppkeyParam,
        }),
      }),
      providesTags: ['Teams'],
    }),
    createTeam: builder.mutation<void, CreateTeam>({
      query: (payload) => ({
        url: getCommonUrl({
          queryString: '/teams',
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${payload.short_code}`,
        }),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Teams'],
    }),
    createTeamLeader: builder.mutation<void, CreateTeamLeader>({
      query: ({ id, employee_id, short_code }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}/assign-team-leader`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: { employee_id },
      }),
      invalidatesTags: ['TeamEmployees'],
    }),
    deleteTeam: builder.mutation<void, { id: number; short_code: string }>({
      query: ({ id, short_code }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'DELETE',
      }),
      invalidatesTags: ['Teams'],
    }),
    updateTeam: builder.mutation<void, UpdateTeam>({
      query: (payload) => ({
        url: getCommonUrl({
          queryString: `/teams/${payload.id}`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${payload.short_code}`,
        }),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Teams'],
    }),
    updateEmployeeProfilePicture: builder.mutation<
  void,
  UpdateEmployeeProfilePictureProps
>({
  query: ({ id, short_code, profile_picture, id_type }) => {
    const formData = new FormData();
    formData.append('profile_picture', profile_picture);
    
    // Add id_type if specified
    if (id_type) {
      formData.append('id_type', id_type);
    }
    
    return {
      url: getCommonUrl({
        queryString: `/employees/${id}/update-profile-picture`,
        params: staffAdminAppkeyParam,
        query: `&venue_short_code=${short_code}`,
      }),
      method: 'POST',
      body: formData,
      formData: true,
    };
  },
  invalidatesTags: ['Employees'],
}),
    removeTeamEmployee: builder.mutation<void, DeleteTeamEmployee>({
      query: ({ short_code, id, employee_ids }) => ({
        url: getCommonUrl({
          queryString: `/teams/${id}/remove-employees`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: { employee_ids },
      }),
      invalidatesTags: ['TeamEmployees'],
    }),
    updateEmployeeStatus: builder.mutation<
      void,
      { id: number; status: string; short_code: string }
    >({
      query: ({ id, short_code, status }) => ({
        url: getCommonUrl({
          queryString: `/employees/${id}/status`,
          params: staffAdminAppkeyParam,
          query: `&venue_short_code=${short_code}`,
        }),
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Employees'],
    }),
    getEmployeeTimeData: builder.query<any, IdShortCodeProps>({
      query: ({ id, venue_short_code }) => ({
        url: getCommonUrl({
          queryString: `/employee/${id}/time-data`,
          query: `&venue_short_code=${venue_short_code}`,
          params: staffAdminAppkeyParam,
        }),
      }),
    }),
  }),
})

export const {
  useGetEmployeesQuery,
  useGetTeamEmployeesQuery,
  useGetDepartmentsQuery,
  useLazyGetDepartmentsQuery,
  useLazyGetEmployeesQuery,
  useGetTeamsQuery,
  useLazyGetTeamsQuery,
  useGetTeamByIdQuery,
  useGetDepartmentsByTeamQuery,
  useGetTeamleadersQuery,
  useLazyGetTeamleadersQuery,
  useGetOperationsManagerQuery,
  useLazyGetOperationsManagerQuery,
  //
  useGetEmployeeByIdQuery,
  useCreateTeamEmployeeMutation,
  useCreateTeamOfficeManagerMutation,
  useCreateTeamLeaderMutation,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useUpdateEmployeeMutation,
  useDeleteDepartmentMutation,
  useCreateTeamMutation,
  useAddEmployeeMutation,
  useDeleteTeamMutation,
  useUpdateTeamMutation,
  useRemoveTeamEmployeeMutation,
  useUpdateEmployeeStatusMutation,
    useGetEmployeeTimeDataQuery,
    useUpdateEmployeeProfilePictureMutation
} = staffApi
